import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {CAApi, Match, type PendingRequest} from "components/src/api/admin/generated";
import {CreateCADialog} from "components/src/ca/CreateCADialog";
import {FinalizePendingRequestDialog} from "components/src/ca/FinalizePendingRequestDialog";
import {RequestCertificateDialog} from "components/src/ca/RequestCertificateDialog";
import {SelectCADialog} from "components/src/ca/SelectCADialog";
import {ActionsDropdown} from "components/src/common/ActionsDropdown";
import {ClearSelection} from "components/src/common/ClearSelection";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {Download} from "components/src/common/Download";
import {DialogCloseCause} from "components/src/common/Enums";
import {InvertSelection} from "components/src/common/InvertSelection";
import {Pager} from "components/src/common/Pager";
import {RowsPerPage} from "components/src/common/RowsPerPage";
import {Truncate} from "components/src/common/Truncate";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageAlert} from "components/src/layout/PageAlert.tsx";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageFooter} from "components/src/layout/PageFooter.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {DateUtils} from "components/src/utils/DateUtils";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {
    Bin as DeleteIcon,
    CardShield as SMIMEIcon,
    CloudUpload as UploadCSRIcon,
    DatabaseExport as ExportIcon,
    Plus as RequestCertificateIcon,
    Refresh as RescheduleIcon,
    SecurityPass as SelectLocalCAIcon,
    ShieldPlusIn as CreateLocalCAIcon
} from "iconoir-react";
import {useCallback, useEffect, useState} from 'react'
import {Dropdown} from "react-bootstrap";
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Nav from "react-bootstrap/Nav";
import Navbar from 'react-bootstrap/Navbar';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const caApi = new CAApi(ApiConfig.config());

export const Ca = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The loaded pending requests
     */
    const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
    /**
     * The total number of pending requests
     */
    const [pendingRequestCount, setPendingRequestCount] = useState<number>(0);
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the current page
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of pending request to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected pending requests
     */
    const [selectedPendingRequests, setSelectedPendingRequests] = useState<PendingRequest[]>([]);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * Incremented whenever the data should be reloaded from the back-end. A counter is used instead of a boolean
     * because every increment is a new value, so a reload is never dropped and the load effects run exactly once
     * per reload request
     */
    const [reloadCounter, setReloadCounter] = useState<number>(0);
    /**
     * Forces the data to be reloaded from the back-end
     */
    const forceReload = useCallback(() => setReloadCounter(counter => counter + 1), []);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * The field which is searched on
     */
    const [activeSearchValue, setActiveSearchValue] = useState<string>();
    /**
     * The field of the search box. This value will be copied to the activeSearchValue when apply is clicked
     */
    const [searchValue, setSearchValue] = useState<string>();
    /**
     * If true, the create CA dialog will be shown
     */
    const [showCreateCADialog, setShowCreateCADialog] = useState<boolean>(false);
    /**
     * If true, the select CA dialog will be shown
     */
    const [showSelectCADialog, setShowSelectCADialog] = useState<boolean>(false);
    /**
     * If true, the Finalize Pending Request dialog will be shown
     */
    const [showFinalizePendingRequestDialog, setShowFinalizePendingRequestDialog] = useState<boolean>(false);
    /**
     * If true, the Request Certificate dialog will be shown
     */
    const [showRequestCertificateDialog, setShowRequestCertificateDialog] = useState<boolean>(false);
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() => {
        logger.debug("Getting pending requests count")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        let callback;

        if ((activeSearchValue ?? "").length > 0) {
            callback = async () => {
                return await caApi.cAControllerGetPendingRequestsByEmailCount(`%${activeSearchValue ?? ""}%`, Match.Like);
            };
        } else {
            callback = async () => {
                return await caApi.cAControllerGetPendingRequestsCount();
            };
        }

        callback().then(response => {
            if (superseded) {
                return;
            }

            setPendingRequestCount(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting pending requests", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        setSpinnerStateCounter,
        activeSearchValue,
        reloadCounter,
        t]);

    useEffect(() => {
        logger.debug("Getting pending requests")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        // Set when this effect is cleaned up, i.e., when a newer request has superseded this one. The response of a
        // superseded request must be ignored because it can arrive after the response of the newer request
        let superseded = false;

        // reset state when we load new data
        setSelectedPendingRequests([]);
        // Note: do not reset errorMessage because otherwise the message might get lost when re-rendering
        //       The error message can be closed by clicking the close icon

        let callback;

        if ((activeSearchValue ?? "").length > 0) {
            callback = async () => {
                return await caApi.cAControllerGetPendingRequestsByEmail(`%${activeSearchValue ?? ""}%`, Match.Like,
                    (currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        } else {
            callback = async () => {
                return await caApi.cAControllerGetPendingRequests((currentPage - 1) * rowsPerPage, rowsPerPage);
            };
        }

        callback().then(response => {
            if (superseded) {
                return;
            }

            setPendingRequests(response.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting pending requests", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);

                if (superseded) {
                    return;
                }

                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })

        return () => {
            superseded = true;
        }
    }, [navigate,
        setSpinnerStateCounter,
        activeSearchValue,
        reloadCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const deleteSelected = () => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        caApi.cAControllerDeletePendingRequests(selectedPendingRequests.map(r => r.id))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting pending requests", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setShowDeleteConfirm(false);
            setSelectedPendingRequests([]);
        })
    }


    const recheduleSelected = () => {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        caApi.cAControllerRescheduleRequests(selectedPendingRequests.map(r => r.id))
            .then(() => {
                forceReload();
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error reschedule pending requests", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            }).finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
            setSelectedPendingRequests([]);
        })
    }

    /**
     * Called when a pending request is selected (i.e., when the checkbox is checked)
     */
    const onSelectPendingRequest = (checked: boolean, pendingRequest: PendingRequest) =>
    {
        setSelectedPendingRequests(checked ?
            [...selectedPendingRequests, pendingRequest] :
            selectedPendingRequests.filter(p => p.id !== pendingRequest.id)
        );
    }

    /**
     * Inverts the selection of pending requests by updating the list of selected pending requests.
     * Each previously unselected pending request will be selected, and each previously
     * selected pending request will be unselected.
     */
    const invertSelection = () => {
        setSelectedPendingRequests([...pendingRequests].filter(p => !selectedPendingRequests.includes(p)));
    }

    /**
     * Called when the CreateCADialog is closed
     */
    const onCreateCADialogClose = () => {
        setShowCreateCADialog(false);
    }

    /**
     * Called when the SelectCADialog is closed
     */
    const onSelectCADialogClose = () => {
        setShowSelectCADialog(false);
    }

    const onFinalizePendingRequestDialogClose = (closeCause: DialogCloseCause) => {
        setShowFinalizePendingRequestDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    const onRequestCertificateDialogClose = (closeCause: DialogCloseCause) => {
        setShowRequestCertificateDialog(false);

        if (closeCause === DialogCloseCause.Succes) {
            forceReload();
        }
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () => {
        // disable the Download component
        setDownload(false);
        setSelectedPendingRequests([]);
    }

    const downloadSelectedCSRs = () => {
        setBlob(new Blob([selectedPendingRequests.map(r => r.pemEncodedRequest).join("\r\n")], {type: "application/x-pem-file"}));

        // enable the Download component
        setDownload(true);
    }

    interface ColumnProps {
        value?: string
        children?: React.ReactNode
        className?: string
    }

    const Column = ({value, children, className}: ColumnProps) => {
        return (
            <td className={"user-select-all " + className}>{children}<Truncate value={value} maxLength={60}/></td>
        )
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("ca.are-you-sure-delete-requests")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showCreateCADialog && <CreateCADialog onClose={onCreateCADialogClose}/>}
            {showSelectCADialog && <SelectCADialog onClose={onSelectCADialogClose}/>}
            {showFinalizePendingRequestDialog &&
                <FinalizePendingRequestDialog onClose={onFinalizePendingRequestDialogClose}/>}
            {showRequestCertificateDialog &&
                <RequestCertificateDialog onClose={onRequestCertificateDialogClose}/>}

            <div className="d-flex flex-column h-100">
                <PageHeader icon={<SMIMEIcon />} title={t("ca.title")} onClickReload={forceReload}/>

                <PageAlert message={errorMessage} setMessage={setErrorMessage}/>

                <div className="px-4">
                    <Row>
                        <div className="d-flex flex-row align-items-center mb-4">
                            <div className="d-flex flex-fill justify-content-end align-items-center">
                                <div className="d-xl-flex">
                                    <InputGroup>
                                        <Form.Control
                                            aria-label="Email search value"
                                            type="search"
                                            placeholder={t("ca.email")}
                                            value={searchValue}
                                            onChange={e => setSearchValue(e.target.value)}
                                        />
                                        <Button
                                            variant="primary"
                                            onClick={() => setActiveSearchValue(searchValue)}
                                            disabled={activeSearchValue === searchValue}>
                                            {t("ca.search")}
                                        </Button>
                                    </InputGroup>
                                </div>

                                <div className="ms-4"/>

                                <ActionsDropdown>
                                    <Dropdown.Item onClick={() => setShowCreateCADialog(true)}>
                                        <CreateLocalCAIcon className="text-success"/> {t("ca.create-local-ca")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setShowSelectCADialog(true)}>
                                        <SelectLocalCAIcon className="text-success"/> {t("ca.select-local-ca")}
                                    </Dropdown.Item>
                                    <Dropdown.Divider/>
                                    <Dropdown.Item onClick={() => setShowFinalizePendingRequestDialog(true)}>
                                        <UploadCSRIcon className="text-success"/> {t("ca.upload-csr-certificate")}
                                    </Dropdown.Item>
                                    <Dropdown.Divider/>
                                    <Dropdown.Item onClick={() => setShowRequestCertificateDialog(true)}>
                                        <RequestCertificateIcon className="text-success"/> {t("ca.request-certificate")}
                                    </Dropdown.Item>
                                </ActionsDropdown>
                            </div>
                        </div>
                    </Row>

                    <div className="fs-5 text-muted mb-2">{t("ca.pending-certificate-requests")}</div>
                </div>

                <PageContent className="flex-grow-1 min-h-0">

                    <Row className="h-100 w-100">
                        <Col className="h-100 overflow-auto">
                            <Table className="sticky sticky-x caption-top" bordered hover>
                                <thead className="sticky-top">
                                <tr className="text-nowrap">
                                    {selectedPendingRequests.length === 0 ?
                                        <>
                                            <th className="th-w-min">
                                                {/* Add icon to first column which will invert selection when clicked */}
                                                <InvertSelection invertSelectionEvent={invertSelection}/>
                                            </th>
                                            <th>{t("ca.email")}</th>
                                            <th>{t("ca.creation-date")}</th>
                                            <th>{t("ca.subject")}</th>
                                            <th>{t("ca.validity-days")}</th>
                                            <th>{t("ca.signature-algorithm")}</th>
                                            <th>{t("ca.key-length")}</th>
                                            <th>{t("ca.handler")}</th>
                                            <th>{t("ca.iteration")}</th>
                                            <th>{t("ca.last-updated")}</th>
                                            <th>{t("ca.next-update")}</th>
                                            <th>{t("ca.message")}</th>
                                            <th>{t("ca.info")}</th>
                                            <th>{t("ca.id")}</th>
                                        </>
                                        :
                                        <th colSpan={14}>
                                            <Navbar className="bg-body-tertiary ps-2">
                                                {/* Add icon to first column which will clear the selection when clicked */}
                                                <ClearSelection clearSelectionEvent={() => setSelectedPendingRequests([])} checked={true}/>

                                                <Navbar.Text className="ms-2">{selectedPendingRequests.length} selected</Navbar.Text>

                                                <div className="vr ms-4 me-2"></div>

                                                <Nav>
                                                    <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                        <DeleteIcon className="text-danger"/> {t("ca.delete-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => recheduleSelected()}>
                                                        <RescheduleIcon className="text-success"/> {t("ca.reschedule-selected")}
                                                    </Nav.Link>
                                                    <Nav.Link onClick={() => downloadSelectedCSRs()}>
                                                        <ExportIcon className="text-success"/>  {t("ca.download-selected-csrs")}
                                                    </Nav.Link>
                                                </Nav>
                                            </Navbar>
                                        </th>
                                    }
                                </tr>
                                </thead>

                                <tbody>
                                {pendingRequests.map((request: PendingRequest) => (
                                    <tr key={request.id}>
                                        <Column className="td-w-min">
                                            {/* Add selection checkbox to first column */}
                                            <Form.Check
                                                type="checkbox"
                                                className="me-0"
                                                checked={selectedPendingRequests.find((sreq) =>  sreq.id === request.id) !== undefined}
                                                onChange={(e) => onSelectPendingRequest(e.target.checked, request)}
                                                inline
                                            />
                                        </Column>
                                        <Column value={request.email}/>
                                        <Column value={DateUtils.fromTimestampToLocaleDateStringShort(request.creationDate)}/>
                                        <Column value={request.subject}/>
                                        <Column value={`${request.validityDays}`}/>
                                        <Column value={request.signatureAlgorithm}/>
                                        <Column value={`${request.keyLength}`}/>
                                        <Column value={request.certificateRequestHandler}/>
                                        <Column value={`${request.iteration}`}/>
                                        <Column value={DateUtils.fromTimestampToLocaleDateStringShort(request.lastUpdated)}/>
                                        <Column value={DateUtils.fromTimestampToLocaleDateStringShort(request.nextUpdate)}/>
                                        <Column value={request.message}/>
                                        <Column value={request.info}/>
                                        <Column value={request.id}/>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </PageContent>

                <PageFooter
                    left={<RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage} setCurrentPage={setCurrentPage}/>}
                    right={<Pager itemCount={pendingRequestCount ?? 0}
                                  currentPage={currentPage}
                                  setCurrentPage={setCurrentPage}
                                  maxItemsPerPage={rowsPerPage}
                    />}
                />
            </div>

            {download && blob !== undefined && (
                <Download blob={blob}
                          filename="csr.pem"
                          onDownload={onDownload}/>
            )}
        </>
    )
}
